import { TransferCommonParameters, TransferEmailParameters, TransferLinkParameters } from '@app/client/uploader/interfaces/transfer';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { GetDropboxPreviewOutput } from '@smash-sdk/transfer/ejs/01-2024';
import { CreateDropboxTransfer, TransferFiles, UploadInput, UploaderError, UploaderStatus } from '@smash-sdk/uploader';
import { Language } from '../types/Language';
import { cleanDeep } from './clean-deep';
import { CloudfrontError } from './cloudfront';
import { getLanguageWithFallback } from './language';
import { Constant } from 'src/constant';

export interface ErrorDetails {
    name?: string;
    type?: string;
    reason?: string;
    expected?: string;
    given?: number;
    min?: number;
    max?: number;
    primary?: string;
    secondary?: string;
}

export function parseUploaderError(error: UploaderError) {
    return {
        name: error.name,
        error: error.error,
        details: error.details,
        message: error.message,
        requestId: error.requestId,
        code: error.code,
    };
}

// Error names for which the prepare screen's handleUploadError() (desktop:
// prepare.component.ts, mobile: prepare-mobile.component.ts) opens an in-place modal that lets
// the user answer and resubmit (e.g. re-enter a password) instead of showing a fatal-error
// screen. When the current error is one of these, the caller must keep the wizard on its
// parameters step/route so the modal's resubmit path stays available -- on desktop,
// prepare.component.ts's uploadError$ subscription skips the /send navigation; on mobile,
// uploader-mobile.component.ts skips the SEND step transition.
export const RECOVERABLE_MODAL_ANSWERABLE_ERROR_NAMES: ReadonlySet<string> = new Set([
    'PasswordRequiredError',
    'EmailNotAllowedError',
    'InvalidSubscriptionError',
]);

// Error names surfaced INLINE by the form control they relate to -- e.g. the link form's
// customUrl field showing UPLOADER_LINK_ALREADY_EXISTS when CreateTransfer rejects a
// slugified-from-title customUrl that collides with an existing one (desktop:
// link-form.component.html, mobile: upload-mobile-link-parameters.component.html). Like
// RECOVERABLE_MODAL_ANSWERABLE_ERROR_NAMES, the caller must not navigate away from the
// parameters step for these: doing so unmounts the form before the inline message can ever be
// seen. Unlike that set, no modal opens here -- the field-level message already reacts to
// uploadError$ directly, so handleUploadError() has nothing to do for these names.
// Sound only because buildUploadInput() below enforces that customUrl is sent in Link mode
// exclusively -- see the guard before its return -- so this error can only arrive while the Link
// form (the sole inline renderer for it) is mounted.
export const RECOVERABLE_INLINE_FORM_ERROR_NAMES: ReadonlySet<string> = new Set([
    // Single source for the wire error name, shared with the mobile auto-expand subscription
    // (upload-mobile-link-parameters.component.ts) which keys off the same Constant.
    Constant.uploaderErrorType.customUrlAlreadyInUse,
]);

// Whether the send view (app-upload-progress / app-send-mobile) has a meaningful upload state to
// render for `status`. The send-route guards (SendGuard / DropboxSendGuard) allow entry to /send
// for these, and bounce back to the uploader landing only when there is genuinely no upload in
// flight (initial `Pending`, a `Canceled` reset, or none). Crucially this includes `Error`: a
// CreateTransfer failure transitions Pending -> Starting -> Error (it never reaches `Started`), and
// the send view renders that error state (upload-progress's `uploadFailed`). The guards previously
// allowed ONLY `Started`, so a failed upload was silently bounced to the site root with no error UI
// (prepare.component navigates to /send on Error since ac0aade2, but the guard cancelled it).
export function hasRenderableUploadStatus(status: UploaderStatus | null | undefined): boolean {
    return !!status
        && status !== UploaderStatus.Pending
        && status !== UploaderStatus.Canceled;
}

export function isUploaderFatalError(error: UploaderError | CloudfrontError<UploaderError>) {
    const fatalErrors = [
        'EmptyFileListError',
        'FileReaderAbortError',
        'FileReaderNotReadableError',
        'FileReaderSecurityError',
        'FileReaderUnknownError',
        'FileReaderNotFoundError',
        'InvalidParameterError',
        'UnauthorizedError',
        'UnvalidTokenError',
        'FileSystemAbortError',
        'FileSystemNotFoundError',
        'FileSystemPermissionDeniedError',
        'FileSystemUnknownError',
        'InvalidAvailabilityDurationError',
        'InvalidDeliveryError',
        'MissingReceiversError',
        'MissingSenderError',
        'UnsupportedFileSourceError',
        'UnsupportedFileTypeError',
        'UsageExceededError',
        'OwnerUsageExceededError',
        'NetworkError',
        'TransferIsInErrorError',
        'TransferNotFoundError',
        'UnknownError',
    ];
    return fatalErrors.includes(error.name);
}

export function shouldStartNewUpload(status: UploaderStatus) {
    return [
        UploaderStatus.Canceled,
        UploaderStatus.Error,
    ].includes(status);
}

export function shouldResumeUpload(status: UploaderStatus) {
    return [
        UploaderStatus.Paused,
        UploaderStatus.Queued,
        UploaderStatus.Resumable,
    ].includes(status);
}

export function isValidStatusToStartUpload(status: UploaderStatus) {
    return [
        UploaderStatus.Paused,
        UploaderStatus.Queued,
        UploaderStatus.Resumable,
        UploaderStatus.Canceled,
        UploaderStatus.Error,
    ].includes(status);
}

// Whether the prepare screen's submit control (desktop: the send-transfer button's spinner +
// disabled state in upload-parameters-box.component.html; mobile: the app-upload-mobile-footer's
// [loading] binding) should render as "busy". This was historically gated on Starting alone, so
// once the SDK transitioned to Queued or Resumable the spinner vanished and the button/footer
// went back to its idle look -- even though nothing had actually started yet and the transfer
// was sitting queued/awaiting a resume. That silence is exactly what let a genuinely-queued
// transfer look abandoned during the 2026-07-17 clock-skew incident; this defense-in-depth
// follow-up keeps the busy state visible through Queued and Resumable too, on both platforms.
//
// Started and Finishing were then found missing for the same reason, and they are the states the
// user spends the MOST time in: Started is "the upload pipeline is actively transferring file
// parts", Finishing is "sealing the transfer on the server". While they were excluded, every
// control gated on this predicate went idle DURING the transfer -- most visibly the desktop
// delivery tabs, which lost their [disabled] and accepted a click that moved the 'tab-selected'
// highlight even though buildUploadInput() had already pinned the mode at send time. The mode
// silently did not change, so the highlight was lying about what the transfer would do.
// The mobile Email footer had already patched this locally with an extra
// `|| uploadStatus === UploaderStatus.Started`, while the mobile Link footer and the whole desktop
// box had not -- the divergence is what identified the shared predicate as the real gap, so the
// local patch is now removed in favour of this single definition.
//
// The set is deliberately the SDK's own "active phase" trio (Starting / Started / Finishing) plus
// the two waiting states (Queued / Resumable). The SDK exports an equivalent `isActivePhaseStatus`
// from globals/constant, but its public entry point re-exports only UploaderEvents and
// UploaderStatus, so reusing it would mean a deep import into the package's internals.
// Paused and NetworkPaused are deliberately EXCLUDED and must stay excluded: the transfer is
// genuinely halted there, so a spinner would misreport it as progressing -- and while Paused the
// prepare screen's submit is deliberately live (isValidStatusToStartUpload includes Paused), so
// marking it busy would dead-lock the honored edit-while-paused path. The cost is that the delivery
// tabs are clickable again while paused. Closing that needs a LOCK predicate distinct from this
// "renders as busy" one, not a widening of this set; it is tracked, together with a second
// browser-Back route on mobile, in docs/uploader-delivery-mode-lock-followups.md.
export function isUploadStatusBusy(status: UploaderStatus | null | undefined): boolean {
    return status === UploaderStatus.Starting
        || status === UploaderStatus.Started
        || status === UploaderStatus.Queued
        || status === UploaderStatus.Resumable
        || status === UploaderStatus.Finishing;
}

export function buildUploadInput(
    {
        domain,
        language,
        commonParameters,
        linkParameters,
        emailParameters,
        uploadFormMode,
        dropbox,
        files,
    }: {
        domain: GetRecordOutput['record'],
        language: Language,
        commonParameters: TransferCommonParameters,
        linkParameters: TransferLinkParameters,
        emailParameters: TransferEmailParameters,
        uploadFormMode: 'Email' | 'Link' | 'Slack',
        dropbox: GetDropboxPreviewOutput['dropbox'],
        files: {
            name: string;
            file: File | string;
        }[];
    }): UploadInput {

    const uploadInput: UploadInput = {
        files,
        teamId: domain.team,
        language: getLanguageWithFallback(language),
        password: commonParameters.password,
        preview: commonParameters.preview,
        notification: commonParameters.notification,
        availabilityDuration: commonParameters.availabilityDuration,
        promotion: commonParameters.promotion,
        customization: commonParameters.customization,
        title: commonParameters.title,
    };

    if (uploadFormMode === 'Email') {
        uploadInput.description = emailParameters.description;
        uploadInput.delivery = { type: 'Email', ...emailParameters.delivery };
        uploadInput.notification = {
            ...uploadInput.notification,
            link: { enabled: false },
            receiver: { enabled: true },
        };
    }

    if (uploadFormMode === 'Link') {
        uploadInput.customUrl = linkParameters.customUrl?.trim();
        uploadInput.accessTracking = linkParameters.accessTracking;
        uploadInput.delivery = { type: 'Link', ...linkParameters.delivery };
        uploadInput.notification = {
            ...uploadInput.notification,
            sender: { enabled: false },
            receiver: { enabled: false },
        };
    }

    if (dropbox?.id) {
        (uploadInput as TransferFiles & CreateDropboxTransfer).dropboxId = dropbox.id;
        uploadInput.delivery = {
            type: 'Dropbox',
            // Defensive dereference: after ResetUpload the store can transiently hold a null
            // delivery / missing sender (the sender is preserved by the ResetUpload reducer since
            // the re-upload fix, but any future sender-less state must surface as the SDK's
            // MissingSenderError — a handled error state — instead of an uncaught TypeError here
            // that kills the NgRx effects stream and leaves the wizard frozen with no error UI).
            // cleanDeep() below strips the empty sender object, so the SDK sees no sender at all.
            sender: {
                name: emailParameters.delivery?.sender?.name,
                email: emailParameters.delivery?.sender?.email
            },
        };
        // Same defensive rationale as the sender above: notification is null until the
        // parameters-form submit dispatches SetTransferConfigurationParameters, so a state that
        // skipped it must not TypeError here; cleanDeep() strips the empty object.
        uploadInput.notification = {
            download: commonParameters.notification?.download,
            noDownload: commonParameters.notification?.noDownload,
            sender: commonParameters.notification?.sender,
            receiver: commonParameters.notification?.receiver,
        };
        delete uploadInput.accessTracking;
        delete uploadInput.customUrl;
    }

    // Load-bearing invariant for RECOVERABLE_INLINE_FORM_ERROR_NAMES (top of this file): customUrl
    // goes on the wire ONLY in Link mode, so a CustomUrlAlreadyInUseError (409) can only ever come
    // back while the Link form -- the sole inline renderer for it -- is mounted. The carve-outs in
    // prepare.component.ts / prepare-mobile.component.ts / uploader-mobile.component.ts skip the fatal
    // modal AND the nav/step change for that error name WITHOUT a mode check; a customUrl sent in any
    // other mode would surface its 409 as pure silence there. Enforce the invariant here rather than
    // assume it -- if you are adding customUrl to a non-Link mode, give those carve-outs a mode gate first.
    if (uploadFormMode !== 'Link') {
        delete uploadInput.customUrl;
    }

    return cleanDeep(uploadInput) as UploadInput;
}
